import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import {
    Plus, Trash, FloppyDisk, CircleNotch, Sparkle, ImageSquare, UploadSimple,
    WarningCircle, CheckCircle, ListChecks, ArrowClockwise, CaretDown, CaretRight,
    Drop, FirstAidKit, Cube, Buildings, House, Wrench, Package, ClipboardText, Robot, Camera,
} from "@phosphor-icons/react";

const HEALTH = {
    on_track: { label: "On track", color: "var(--rom-green)", bg: "rgba(0,133,63,0.10)" },
    at_risk: { label: "At risk", color: "#d97706", bg: "rgba(217,119,6,0.10)" },
    off_track: { label: "Off track", color: "#dc2626", bg: "rgba(220,38,38,0.10)" },
};

export function HealthBadge({ health }) {
    if (!health) return null;
    const h = HEALTH[health.status] || HEALTH.on_track;
    return (
        <span data-testid="project-health-badge"
            title={(health.reasons || []).join(" · ")}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ color: h.color, background: h.bg }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: h.color }} />
            {h.label}{health.overridden ? " ·  manual" : ""}
        </span>
    );
}

export function ProgressControl({ project, onSave }) {
    const auto = project.auto_progress_pct;
    const manual = !!project.progress_manual;
    const pct = project.progress_pct ?? 0;
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(pct);
    useEffect(() => { setVal(project.progress_pct ?? 0); }, [project.progress_pct]);

    const barColor = pct >= 75 ? "var(--rom-green)" : pct >= 40 ? "var(--rom-orange)" : "var(--rom-navy)";
    return (
        <div className="w-full sm:w-72" data-testid="project-progress">
            <div className="flex items-center justify-between text-[11px] text-[var(--rom-text-2)] mb-1">
                <span className="font-semibold uppercase tracking-wider">% Complete</span>
                <span className="flex items-center gap-2">
                    <span className="font-bold text-sm" style={{ color: barColor }} data-testid="progress-pct">{Math.round(pct)}%</span>
                    <button onClick={() => setEditing((e) => !e)} className="text-[var(--rom-orange-dark)] hover:underline" data-testid="progress-edit-toggle">
                        {editing ? "close" : manual ? "manual" : "auto"}
                    </button>
                </span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
            </div>
            {editing && (
                <div className="mt-3 p-3 rounded-[6px] border border-[var(--rom-border)] bg-slate-50 space-y-2">
                    <label className="flex items-center gap-2 text-xs font-semibold">
                        <input type="radio" checked={!manual} onChange={() => onSave({ progress_manual: false })} data-testid="progress-mode-auto" />
                        Auto {auto != null ? `(${Math.round(auto)}% from tasks/phases)` : "(no tasks yet)"}
                    </label>
                    <label className="flex items-center gap-2 text-xs font-semibold">
                        <input type="radio" checked={manual} onChange={() => onSave({ progress_manual: true, progress_pct: val })} data-testid="progress-mode-manual" />
                        Set manually
                    </label>
                    {manual && (
                        <div className="flex items-center gap-2 pl-6">
                            <input type="range" min={0} max={100} step={5} value={val} onChange={(e) => setVal(Number(e.target.value))}
                                onMouseUp={() => onSave({ progress_manual: true, progress_pct: val })}
                                onTouchEnd={() => onSave({ progress_manual: true, progress_pct: val })}
                                className="flex-1" data-testid="progress-slider" />
                            <span className="text-xs font-bold w-10 text-right">{val}%</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ── Tasks tab ─────────────────────────────────────────────────────────── */
const PRIORITIES = ["low", "medium", "high"];
const TASK_STATUS = ["not_started", "in_progress", "blocked", "done"];
const STATUS_CHIP = {
    not_started: "chip-gray", in_progress: "chip-orange", blocked: "chip-red", done: "chip-green",
};

export function TasksTab({ project, onSave, canEdit }) {
    const [rows, setRows] = useState(project.tasks || []);
    const [employees, setEmployees] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setRows(project.tasks || []); setDirty(false); }, [project]);
    useEffect(() => { endpoints.employees().then((r) => setEmployees(r.data || [])).catch(() => {}); }, []);

    function update(i, k, v) {
        setRows((rs) => rs.map((r, idx) => {
            if (idx !== i) return r;
            const nr = { ...r, [k]: v };
            if (k === "status") nr.progress_pct = v === "done" ? 100 : (v === "not_started" ? 0 : (r.progress_pct || 0));
            if (k === "assignee_employee_id") nr.assignee_name = employees.find((e) => e.id === v)?.employee_name || "";
            return nr;
        }));
        setDirty(true);
    }
    function addRow() {
        setRows((rs) => [...rs, { title: "", status: "not_started", priority: "medium", progress_pct: 0, due_date: "", assignee_employee_id: "", assignee_name: "" }]);
        setDirty(true);
    }
    function delRow(i) { setRows((rs) => rs.filter((_, idx) => idx !== i)); setDirty(true); }
    async function save() { setSaving(true); try { await onSave({ tasks: rows }); setDirty(false); } finally { setSaving(false); } }

    const done = rows.filter((r) => r.status === "done").length;
    const overdue = rows.filter((r) => r.status !== "done" && r.due_date && r.due_date < new Date().toISOString().slice(0, 10)).length;

    return (
        <div className="rom-card" data-testid="tasks-tab">
            <div className="rom-card-head">
                <div className="flex items-center gap-3">
                    <h3 className="flex items-center gap-2"><ListChecks size={17} weight="bold" /> Tasks</h3>
                    <span className="text-xs text-[var(--rom-text-3)]">{done}/{rows.length} done{overdue ? ` · ${overdue} overdue` : ""}</span>
                </div>
                {canEdit && (
                    <div className="flex gap-2">
                        <button onClick={addRow} className="btn btn-ghost btn-sm" data-testid="add-task"><Plus size={13} weight="bold" /> Add task</button>
                        {dirty && <button onClick={save} disabled={saving} className="btn btn-primary btn-sm" data-testid="save-tasks">{saving ? <CircleNotch size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="bold" />} Save</button>}
                    </div>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="rom-table">
                    <thead><tr>
                        <th>Task</th><th>Assignee</th><th>Due</th><th>Priority</th><th>Status</th><th style={{ minWidth: 150 }}>Progress</th>{canEdit && <th></th>}
                    </tr></thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={canEdit ? 7 : 6} className="text-center py-8 text-[var(--rom-text-3)]">No tasks yet.{canEdit && " Add the first task."}</td></tr>
                        ) : rows.map((r, i) => {
                            const isOverdue = r.status !== "done" && r.due_date && r.due_date < new Date().toISOString().slice(0, 10);
                            return (
                                <tr key={r.id || i} data-testid={`task-row-${i}`}>
                                    <td>{canEdit
                                        ? <input value={r.title || ""} onChange={(e) => update(i, "title", e.target.value)} placeholder="Task title" className="h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs w-full min-w-[160px]" data-testid={`task-title-${i}`} />
                                        : <span className="font-medium">{r.title || "—"}</span>}</td>
                                    <td>{canEdit
                                        ? <select value={r.assignee_employee_id || ""} onChange={(e) => update(i, "assignee_employee_id", e.target.value)} className="h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs bg-white min-w-[120px]">
                                            <option value="">Unassigned</option>
                                            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_name}</option>)}
                                        </select>
                                        : <span>{r.assignee_name || "—"}</span>}</td>
                                    <td>{canEdit
                                        ? <input type="date" value={r.due_date || ""} onChange={(e) => update(i, "due_date", e.target.value)} className={`h-8 px-2 rounded-[4px] border text-xs ${isOverdue ? "border-red-400 text-red-600" : "border-[var(--rom-border)]"}`} />
                                        : <span className={isOverdue ? "text-red-600 font-semibold" : ""}>{r.due_date || "—"}</span>}</td>
                                    <td>{canEdit
                                        ? <select value={r.priority || "medium"} onChange={(e) => update(i, "priority", e.target.value)} className="h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs bg-white capitalize">
                                            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                        : <span className="capitalize">{r.priority || "—"}</span>}</td>
                                    <td>{canEdit
                                        ? <select value={r.status || "not_started"} onChange={(e) => update(i, "status", e.target.value)} className="h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs bg-white" data-testid={`task-status-${i}`}>
                                            {TASK_STATUS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                                        </select>
                                        : <span className={`chip ${STATUS_CHIP[r.status] || "chip-gray"}`}>{(r.status || "").replace("_", " ")}</span>}</td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            {canEdit && r.status !== "done" && r.status !== "not_started"
                                                ? <input type="range" min={0} max={100} step={10} value={r.progress_pct || 0} onChange={(e) => update(i, "progress_pct", Number(e.target.value))} className="w-20" data-testid={`task-progress-${i}`} />
                                                : <div className="w-20 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-[var(--rom-orange)]" style={{ width: `${r.status === "done" ? 100 : (r.progress_pct || 0)}%` }} /></div>}
                                            <span className="text-xs font-semibold w-9 text-right">{r.status === "done" ? 100 : (r.progress_pct || 0)}%</span>
                                        </div>
                                    </td>
                                    {canEdit && <td><button onClick={() => delRow(i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash size={14} /></button></td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ── Photos tab ────────────────────────────────────────────────────────── */
export function PhotosTab({ project, onSave, canEdit }) {
    const [rows, setRows] = useState(project.photos || []);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);
    useEffect(() => { setRows(project.photos || []); setDirty(false); }, [project]);

    async function upload(files) {
        if (!files?.length) return;
        setBusy(true);
        try {
            const added = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("project_id", project.id);
                fd.append("kind", "photo");
                const { data } = await endpoints.uploadFile(fd);
                added.push({ file_id: data.id, file_name: data.original_filename, label: "", caption: "", uploaded_at: new Date().toISOString() });
            }
            const next = [...rows, ...added];
            setRows(next);
            await onSave({ photos: next });
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }
    function update(i, k, v) { setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r)); setDirty(true); }
    async function remove(i) { const next = rows.filter((_, idx) => idx !== i); setRows(next); await onSave({ photos: next }); setDirty(false); }
    async function save() { await onSave({ photos: rows }); setDirty(false); }

    return (
        <div className="rom-card" data-testid="photos-tab">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-2"><ImageSquare size={17} weight="bold" /> Site & damage photos <span className="text-xs text-[var(--rom-text-3)] font-normal">({rows.length})</span></h3>
                <div className="flex gap-2 flex-wrap">
                    {dirty && canEdit && <button onClick={save} className="btn btn-ghost btn-sm" data-testid="save-photos"><FloppyDisk size={13} weight="bold" /> Save labels</button>}
                    {canEdit && (
                        <>
                            <label className="btn btn-primary btn-sm cursor-pointer" data-testid="photo-take">
                                {busy ? <CircleNotch size={13} className="animate-spin" /> : <Camera size={13} weight="bold" />} {busy ? "Uploading…" : "Take photo"}
                                <input type="file" accept="image/*" capture="environment" multiple className="hidden" disabled={busy}
                                    onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; upload(f); }} />
                            </label>
                            <label className="btn btn-ghost btn-sm cursor-pointer" data-testid="photo-upload">
                                {busy ? <CircleNotch size={13} className="animate-spin" /> : <UploadSimple size={13} weight="bold" />} {busy ? "Uploading…" : "Upload photos"}
                                <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" multiple className="hidden" disabled={busy}
                                    onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; upload(f); }} />
                            </label>
                        </>
                    )}
                </div>
            </div>
            <div className="rom-card-body">
                {rows.length === 0 ? (
                    <div className="text-center py-10 text-sm text-[var(--rom-text-3)]">
                        <ImageSquare size={32} className="mx-auto mb-2 text-[var(--rom-text-3)]" />
                        No photos yet. {canEdit && "Upload images of the damage, progress or completed work and label them."}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
                        {rows.map((p, i) => (
                            <div key={p.file_id || i} className="rounded-[8px] border border-[var(--rom-border)] overflow-hidden bg-white" data-testid={`photo-card-${i}`}>
                                <a href={endpoints.fileDownloadUrl(p.file_id)} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-slate-100">
                                    <img src={endpoints.fileDownloadUrl(p.file_id)} alt={p.label || p.file_name} className="w-full h-full object-cover" loading="lazy" />
                                </a>
                                <div className="p-2 space-y-1.5">
                                    {canEdit ? (
                                        <>
                                            <input value={p.label || ""} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label (e.g. Master bath — Cat 3)" className="h-7 px-2 w-full rounded-[4px] border border-[var(--rom-border)] text-xs font-semibold" data-testid={`photo-label-${i}`} />
                                            <input value={p.caption || ""} onChange={(e) => update(i, "caption", e.target.value)} placeholder="Caption / notes" className="h-7 px-2 w-full rounded-[4px] border border-[var(--rom-border)] text-[11px]" />
                                            <button onClick={() => remove(i)} className="text-[11px] text-red-500 hover:underline flex items-center gap-1"><Trash size={11} /> Remove</button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-xs font-semibold truncate">{p.label || p.file_name}</div>
                                            {p.caption && <div className="text-[11px] text-[var(--rom-text-2)]">{p.caption}</div>}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ── AI project summary ───────────────────────────────────────────────── */
const SUM_HEALTH = { on_track: "chip-green", at_risk: "chip-orange", off_track: "chip-red" };

export function AiSummaryCard({ project, onUpdated }) {
    const cached = project.ai_summary;
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    async function generate() {
        setBusy(true); setErr("");
        try {
            const { data } = await endpoints.projectAiSummary(project.id);
            onUpdated?.({ ...project, ai_summary: data });
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="rom-card" data-testid="ai-summary-card" style={{ borderColor: "var(--rom-orange)", borderWidth: 1 }}>
            <div className="rom-card-head">
                <h3 className="flex items-center gap-2"><Sparkle size={17} weight="fill" className="text-[var(--rom-orange)]" /> AI project review</h3>
                <button onClick={generate} disabled={busy} className="btn btn-primary btn-sm" data-testid="generate-ai-summary">
                    {busy ? <CircleNotch size={13} className="animate-spin" /> : cached ? <ArrowClockwise size={13} weight="bold" /> : <Sparkle size={13} weight="fill" />}
                    {busy ? "Inspecting…" : cached ? "Refresh" : "Generate review"}
                </button>
            </div>
            <div className="rom-card-body">
                {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
                {!cached && !busy && (
                    <p className="text-sm text-[var(--rom-text-2)] py-4 text-center">Let the AI inspect the schedule, tasks, P&amp;L and labour and tell you how the project is really running.</p>
                )}
                {busy && !cached && (
                    <div className="flex items-center gap-2 text-sm text-[var(--rom-text-2)] py-4 justify-center"><CircleNotch size={16} className="animate-spin" /> Analysing every part of the project…</div>
                )}
                {cached && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-3 flex-wrap">
                            {cached.status && <span className={`chip ${SUM_HEALTH[cached.status] || "chip-gray"} capitalize`}>{cached.status.replace("_", " ")}</span>}
                            <p className="font-display font-bold text-lg leading-snug flex-1 min-w-[200px]" data-testid="ai-summary-headline">{cached.headline}</p>
                        </div>
                        {cached.highlights?.length > 0 && (
                            <SumBlock icon={<CheckCircle size={15} weight="fill" className="text-[var(--rom-green)]" />} title="What's going well" items={cached.highlights} />
                        )}
                        {cached.risks?.length > 0 && (
                            <SumBlock icon={<WarningCircle size={15} weight="fill" className="text-amber-500" />} title="Risks & red flags" items={cached.risks} />
                        )}
                        {cached.next_actions?.length > 0 && (
                            <SumBlock icon={<ListChecks size={15} weight="bold" className="text-[var(--rom-orange)]" />} title="Recommended next actions" items={cached.next_actions} />
                        )}
                        <div className="text-[11px] text-[var(--rom-text-3)] pt-1">Generated {cached.generated_at ? new Date(cached.generated_at).toLocaleString() : ""}{cached.generated_by ? ` · ${cached.generated_by}` : ""}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

function SumBlock({ icon, title, items }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-1.5">{icon} {title}</div>
            <ul className="space-y-1">
                {items.map((it, i) => <li key={i} className="text-sm text-[var(--rom-text)] flex gap-2"><span className="text-[var(--rom-text-3)]">•</span><span>{it}</span></li>)}
            </ul>
        </div>
    );
}

/* ── W4: generic renderer for any field-event category (equipment, contents,
   checklist, and future mobile capture types) — no per-type parsing needed. */
const GENERIC_HIDDEN_KEYS = new Set([
    "id", "room_id", "job_id", "visit_id", "device_id", "created_at", "technician_id",
    "op_id", "server_id", "kind", "written_back",
]);
function humanizeKey(k) {
    return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function GenericEventRow({ event }) {
    const entries = Object.entries(event).filter(([k, v]) => !GENERIC_HIDDEN_KEYS.has(k) && v !== null && v !== "" && v !== undefined && typeof v !== "object");
    return (
        <li className="text-xs border border-[var(--rom-border)] rounded-[6px] px-2.5 py-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {entries.length === 0 ? <span className="text-[var(--rom-text-3)]">(no details captured)</span> : entries.map(([k, v]) => (
                <span key={k}><span className="text-[var(--rom-text-3)]">{humanizeKey(k)}:</span> <span className="font-medium">{String(v)}</span></span>
            ))}
        </li>
    );
}
function GenericEventList({ title, icon, events }) {
    if (!events || events.length === 0) return null;
    return (
        <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-1.5 flex items-center gap-1">{icon} {title} ({events.length})</div>
            <ul className="space-y-1">{events.map((e, i) => <GenericEventRow key={e.id || i} event={e} />)}</ul>
        </div>
    );
}

/* ── W2: Field documentation viewer (Property → Floor → Room) ──────────── */
function RoomMediaThumb({ media }) {
    const src = endpoints.fieldMediaUrl(media.id);
    if ((media.kind || "photo") === "video") {
        return (
            <a href={src} target="_blank" rel="noreferrer" className="block aspect-square bg-slate-900 rounded-[6px] overflow-hidden flex items-center justify-center text-white text-[10px] font-semibold" data-testid={`field-media-${media.id}`}>
                ▶ Video
            </a>
        );
    }
    return (
        <a href={src} target="_blank" rel="noreferrer" className="block aspect-square bg-slate-100 rounded-[6px] overflow-hidden" data-testid={`field-media-${media.id}`}>
            <img src={src} alt={media.description || media.room_name || "Field photo"} className="w-full h-full object-cover" loading="lazy" />
        </a>
    );
}

function FieldRoomCard({ room }) {
    const [open, setOpen] = useState(false);
    const count = room.moisture.length + room.damage.length + room.media.length + room.equipment.length + room.contents.length + (room.checklist?.length || 0);
    return (
        <div className="border border-[var(--rom-border)] rounded-[8px] bg-white" data-testid={`field-room-${room.room_id}`}>
            <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left" data-testid={`field-room-toggle-${room.room_id}`}>
                <span className="flex items-center gap-2 text-sm font-semibold">
                    {open ? <CaretDown size={13} /> : <CaretRight size={13} />} <House size={14} className="text-[var(--rom-text-3)]" /> {room.room_name}
                    {room.room_type && <span className="chip chip-navy text-[10px] capitalize">{room.room_type}</span>}
                </span>
                <span className="text-xs text-[var(--rom-text-3)]">{count} item{count === 1 ? "" : "s"}</span>
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-3 border-t border-[var(--rom-border)] pt-2">
                    {room.media.length > 0 && (
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-1.5">Photos / video ({room.media.length})</div>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                {room.media.map((m) => <RoomMediaThumb key={m.id} media={m} />)}
                            </div>
                        </div>
                    )}
                    {room.moisture.length > 0 && (
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-1.5 flex items-center gap-1"><Drop size={12} /> Moisture readings ({room.moisture.length})</div>
                            <ul className="space-y-1">
                                {room.moisture.map((m) => (
                                    <li key={m.id} className="text-xs flex items-center gap-2 flex-wrap">
                                        <b>{m.value}{m.unit}</b>
                                        {m.material && <span className="text-[var(--rom-text-2)]">{m.material}</span>}
                                        {m.location && <span className="text-[var(--rom-text-3)]">· {m.location}</span>}
                                        <span className="text-[var(--rom-text-3)]">· {m.technician_name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {room.damage.length > 0 && (
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-1.5 flex items-center gap-1"><FirstAidKit size={12} /> Damage records ({room.damage.length})</div>
                            <ul className="space-y-1">
                                {room.damage.map((d) => (
                                    <li key={d.id} className="text-xs">
                                        <span className="chip chip-orange text-[10px] capitalize mr-1.5">{d.severity || "—"}</span>
                                        <span className="capitalize font-semibold">{d.damage_type}</span>{d.description && <span className="text-[var(--rom-text-2)]"> — {d.description}</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <GenericEventList title="Equipment" icon={<Wrench size={12} />} events={room.equipment} />
                    <GenericEventList title="Contents" icon={<Package size={12} />} events={room.contents} />
                    <GenericEventList title="Checklist submissions" icon={<ClipboardText size={12} />} events={room.checklist} />
                    {count === 0 && (
                        <div className="text-xs text-[var(--rom-text-3)]">No captures logged for this room yet.</div>
                    )}
                </div>
            )}
        </div>
    );
}

export function FieldDocsTab({ project }) {
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [openFloors, setOpenFloors] = useState(() => new Set());

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        endpoints.fieldRoomsTree(project.id)
            .then(({ data }) => { if (!cancelled) { setTree(data); setOpenFloors(new Set(data.floors.slice(0, 1).map((f) => f.floor_name))); } })
            .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [project.id]);

    function toggleFloor(name) {
        setOpenFloors((prev) => { const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next; });
    }

    const totalRooms = tree ? tree.floors.reduce((s, f) => s + f.rooms.length, 0) : 0;
    const hasUnassigned = tree && Object.values(tree.unassigned).some((v) => v.length > 0);
    const isEmpty = tree && totalRooms === 0 && tree.property_scans.length === 0 && !hasUnassigned;

    return (
        <div className="space-y-4">
        <div className="rom-card" data-testid="field-docs-tab">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-2"><Buildings size={17} weight="bold" /> Field documentation
                    {tree && <span className="text-xs text-[var(--rom-text-3)] font-normal">({totalRooms} room{totalRooms === 1 ? "" : "s"})</span>}
                </h3>
            </div>
            <div className="rom-card-body">
                {loading && <div className="text-center py-10 text-sm text-[var(--rom-text-3)]"><CircleNotch size={20} className="animate-spin mx-auto mb-2" /> Loading field capture…</div>}
                {error && <div className="text-sm text-red-600" data-testid="field-docs-error">{error}</div>}
                {!loading && !error && tree && isEmpty && (
                    <div className="text-center py-10 text-sm text-[var(--rom-text-3)]">
                        <Buildings size={32} className="mx-auto mb-2 text-[var(--rom-text-3)]" />
                        No field capture data yet for this job. Once a technician logs a visit from the mobile app, everything they capture — photos, scans, moisture, damage — will appear here automatically.
                    </div>
                )}
                {!loading && !error && tree && !isEmpty && (
                    <div className="space-y-3">
                        {tree.floors.map((floor) => (
                            <div key={floor.floor_name}>
                                <button onClick={() => toggleFloor(floor.floor_name)} className="w-full flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-2" data-testid={`field-floor-toggle-${floor.floor_name}`}>
                                    {openFloors.has(floor.floor_name) ? <CaretDown size={13} /> : <CaretRight size={13} />} {floor.floor_name} <span className="font-normal text-[var(--rom-text-3)]">({floor.rooms.length})</span>
                                </button>
                                {openFloors.has(floor.floor_name) && (
                                    <div className="space-y-2 mb-3">
                                        {floor.rooms.map((room) => <FieldRoomCard key={room.room_id} room={room} />)}
                                    </div>
                                )}
                            </div>
                        ))}
                        {tree.property_scans.length > 0 && (
                            <div>
                                <div className="text-sm font-bold uppercase tracking-wider text-[var(--rom-text-2)] mb-2 flex items-center gap-1.5"><Cube size={14} /> Property scans ({tree.property_scans.length})</div>
                                <div className="space-y-1.5">
                                    {tree.property_scans.map((s) => (
                                        <div key={s.id} className="text-xs border border-[var(--rom-border)] rounded-[6px] p-2 flex items-center gap-3 flex-wrap">
                                            <span>{s.room_count || 0} room(s) · {s.wall_count || 0} wall(s) · {s.opening_count || 0} opening(s)</span>
                                            <span className="text-[var(--rom-text-3)]">· {s.captured_by} · {s.captured_at ? new Date(s.captured_at).toLocaleDateString() : ""}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {hasUnassigned && (
                            <div className="text-xs text-[var(--rom-text-3)] pt-1">
                                Plus {Object.values(tree.unassigned).reduce((s, v) => s + v.length, 0)} captured item(s) not yet linked to a specific room.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
        {project.rom_job_id && <ScopeDraftPanel jobId={project.rom_job_id} />}
        </div>
    );
}

/* ── W6: Evidence-to-scope AI generation — reviewable, never auto-priced ── */
export function ScopeDraftPanel({ jobId }) {
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        endpoints.getFieldScope(jobId)
            .then(({ data }) => { if (!cancelled) setDraft(data?.generated_at ? data : null); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [jobId]);

    async function generate() {
        setGenerating(true); setError("");
        try {
            const { data } = await endpoints.generateFieldScope(jobId);
            setDraft(data);
        } catch (e) { setError(formatApiError(e)); }
        finally { setGenerating(false); }
    }

    return (
        <div className="rom-card" data-testid="scope-draft-panel">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-2"><Robot size={17} weight="bold" /> AI scope draft <span className="text-xs text-[var(--rom-text-3)] font-normal">(from field evidence)</span></h3>
                <button onClick={generate} disabled={generating} className="btn btn-secondary" data-testid="scope-draft-generate-btn">
                    {generating ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} weight="bold" />} {draft ? "Regenerate" : "Generate draft"}
                </button>
            </div>
            <div className="rom-card-body space-y-3">
                <div className="text-[11px] text-[var(--rom-text-3)] bg-slate-50 border border-[var(--rom-border)] rounded-[6px] px-2.5 py-2">
                    Draft only — cites IICRC S500/S520/S700 sections per task, grounded in rooms/moisture/damage already captured on-site. Never auto-applied to pricing; review and use it to inform the ROM manually.
                </div>
                {loading && <div className="text-center py-6 text-sm text-[var(--rom-text-3)]"><CircleNotch size={18} className="animate-spin mx-auto mb-2" /> Loading…</div>}
                {error && <div className="text-sm text-red-600" data-testid="scope-draft-error">{error}</div>}
                {!loading && !draft && !error && (
                    <div className="text-center py-6 text-sm text-[var(--rom-text-3)]" data-testid="scope-draft-empty">
                        No scope draft generated yet. Capture some field evidence (rooms, moisture, damage), then generate one.
                    </div>
                )}
                {draft && (
                    <div className="space-y-3" data-testid="scope-draft-content">
                        {draft.summary && <p className="text-sm text-[var(--rom-text)]">{draft.summary}</p>}
                        {(draft.rooms || []).map((r, i) => (
                            <div key={i} className="border border-[var(--rom-border)] rounded-[8px] p-3" data-testid={`scope-draft-room-${i}`}>
                                <div className="text-sm font-bold mb-1.5">{r.room_name}</div>
                                <ul className="space-y-1.5">
                                    {(r.tasks || []).map((t, j) => (
                                        <li key={j} className="text-xs flex gap-2">
                                            <span className="text-[var(--rom-text-3)]">•</span>
                                            <span><span className="text-[var(--rom-text)]">{t.task}</span> <span className="text-[var(--rom-text-3)] italic">({t.citation})</span></span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        <div className="text-[11px] text-[var(--rom-text-3)]">Generated {draft.generated_at ? new Date(draft.generated_at).toLocaleString() : ""}{draft.generated_by ? ` · ${draft.generated_by}` : ""}</div>
                    </div>
                )}
            </div>
        </div>
    );
}
